import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Link,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { validateEmail, validatePhoneNumber, validateInstagram, validateTikTok } from 'services/verificationService';
import { registerNewUser } from 'services/flaskService';
import { formatPhoneNumber } from 'utils/authUtils';
import { InfluencerLocation } from '@foodfluence/shared';
import { noSameLocation } from 'utils/locationUtils';

// Import the application form component
import ApplicationFormStep from './components/ApplicationFormStep';
import { usePhoneVerification } from 'hooks/usePhoneVerification';
import { ff } from '../../theme';

const SignUpApplicationPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Form data
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [confirmPhoneNumber, setConfirmPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState<string>('+1');
  const [phoneError, setPhoneError] = useState('');
  const [locations, setLocations] = useState<InfluencerLocation[]>([]);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { sendVerificationCode } = usePhoneVerification();

  // Initialize email from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [location]);

  // Initialize reCAPTCHA
  useEffect(() => {
    const recaptchaContainer = document.getElementById('recaptcha-container');
    if (recaptchaContainer) {
      recaptchaContainer.style.visibility = 'hidden';
      setRecaptchaLoaded(true);
    }

    return () => {
      if (recaptchaContainer) {
        recaptchaContainer.style.visibility = 'hidden';
      }
    };
  }, []);

  // Validation function
  const validateApplicationForm = (): boolean => {
    if (!validateEmail(email)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    // Check that at least one social media account is provided and valid
    const hasValidInstagram = instagram && validateInstagram(instagram);
    const hasValidTikTok = tiktok && validateTikTok(tiktok);
    
    if (!hasValidInstagram && !hasValidTikTok) {
      toast({
        title: "Error",
        description: "Please provide at least one valid social media account (Instagram or TikTok).",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    // If Instagram is provided, validate it
    if (instagram && !validateInstagram(instagram)) {
      toast({
        title: "Error",
        description: "Please enter a valid Instagram handle.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    // If TikTok is provided, validate it
    if (tiktok && !validateTikTok(tiktok)) {
      toast({
        title: "Error",
        description: "Please enter a valid TikTok handle.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (!firstName || !lastName) {
      toast({
        title: "Error",
        description: "Please enter both first and last name.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (!locations || locations.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one location.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (!noSameLocation(locations)) {
      toast({
        title: "Error",
        description: "Please remove duplicate locations.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    const hasValidLocation = locations.some(loc => loc.lat && loc.lng);
    if (!hasValidLocation) {
      toast({
        title: "Error",
        description: "Please set at least one location by clicking on the map.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
    const fullConfirmPhoneNumber = formatPhoneNumber(confirmPhoneNumber, countryCode);

    if (fullPhoneNumber !== fullConfirmPhoneNumber) {
      setPhoneError("Phone numbers don't match");
      return false;
    }

    if (!validatePhoneNumber(fullPhoneNumber)) {
      setPhoneError("Please enter a valid phone number");
      return false;
    }

    setPhoneError('');
    return true;
  };

  // Handle form submission
  const handleSubmitApplication = async () => {
    if (!validateApplicationForm()) return;

    const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
    const applicationData = {
        email,
        firstName,
        lastName,
        instagramHandle: instagram,
        tiktokHandle: tiktok,
      phoneNumber: fullPhoneNumber,
      // Flask saves these with the new account (sign-up is signed out, so there is no authed follow-up call).
      locations: locations.map(({ city, state, country, zipCode, lat, lng, maxCollabDistance }) => ({
        city, state, country, zipCode, lat, lng, maxCollabDistance,
      })),
    };

    setIsLoading(true);
    try {
      const result = await registerNewUser(applicationData);

      if (result.success) {
        // Flask saves locations only for a newly created creator; any other outcome (failure, or an
        // inactive-claim path that omits locationsSaved) means they were not stored.
        if (applicationData.locations.length > 0 && result.locationsSaved !== true) {
          toast({
            title: "Warning",
            description: "Account created but there was an issue saving locations. You can add them later in your profile.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }

        // Determine which social media account to verify
        const hasInstagram = instagram && validateInstagram(instagram);
        const hasTikTok = tiktok && validateTikTok(tiktok);
        
        let verificationMessage = "Account created successfully.";
        
        if (hasInstagram && hasTikTok) {
          // If both are provided, prioritize Instagram for now
          verificationMessage += " Now let's verify your Instagram account.";
        } else if (hasInstagram) {
          verificationMessage += " Now let's verify your Instagram account.";
        } else if (hasTikTok) {
          verificationMessage += " Your account has been created successfully.";
        }

        toast({
          title: "Application Submitted!",
          description: verificationMessage,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Only navigate to Instagram verification if Instagram is provided
        if (hasInstagram) {
          navigate('/instagram-verification', {
            state: {
              email,
              instagram,
              influencerID: result.influencerID,
              phoneNumber: fullPhoneNumber,
              returnTo: location.state?.returnTo,
              returnState: location.state?.state
            }
          });
        } else {
          // If only TikTok is provided, skip Instagram verification but still do phone verification
          try {
            await sendVerificationCode(
              fullPhoneNumber,
              recaptchaLoaded,
              { returnTo: location.state?.returnTo || '/collabs', state: location.state?.state }
            );
          } catch (error) {
            console.error('Error sending phone verification code:', error);
          }
        }

      } else {
        toast({
          title: "Error",
          description: result.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      toast({
        title: "Error",
        description: "An error occurred during account creation. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100svh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="white"
      px={{ base: '20px', md: 6 }}
      pt={{ base: 4, md: 8 }}
      pb={{ base: 6, md: 10 }}
    >
      <Box width="100%" maxW="440px">
        <IconButton
          aria-label="Back"
          icon={<FiArrowLeft size={20} />}
          onClick={() => navigate('/login')}
          variant="ghost"
          color={ff.ink}
          minW="44px"
          h="44px"
          ml="-10px"
        />
      </Box>

      <VStack
        spacing={8}
        width="100%"
        maxW="440px"
        flex="1"
        align="stretch"
        justifyContent="space-between"
        pt={6}
      >
        {/* Application form */}
        <VStack spacing={5} align="stretch">
          <ApplicationFormStep
            email={email}
            setEmail={setEmail}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            instagram={instagram}
            setInstagram={setInstagram}
            tiktok={tiktok}
            setTiktok={setTiktok}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            confirmPhoneNumber={confirmPhoneNumber}
            setConfirmPhoneNumber={setConfirmPhoneNumber}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            phoneError={phoneError}
            locations={locations}
            setLocations={setLocations}
            isLoading={isLoading}
            onSubmit={handleSubmitApplication}
          />
        </VStack>

        {/* Bottom section */}
        <Text fontSize="15px" color={ff.text2} textAlign="center">
          Already have an account?{' '}
          <Link color={ff.ink} fontWeight={600} textDecoration="underline" href="/returning-user">Log in now</Link>
        </Text>
      </VStack>
    </Box>
  );
};

export default SignUpApplicationPage;
